import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = signed in
  const [config, setConfig] = useState(null);
  const [authError, setAuthError] = useState(false);

  const refreshUser = useCallback(async (attempt = 0) => {
    setAuthError(false);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      if (e?.response) { setUser(false); return false; }  // server responded (e.g. 401) -> anonymous
      // No response = network/timeout (e.g. backend cold start). Self-heal with a few quick retries
      // BEFORE surfacing an error, so a fresh user never depends on anyone warming the server first.
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        return refreshUser(attempt + 1);
      }
      setAuthError(true);  // still unreachable -> recoverable error with Try Again (never hang)
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      return; // AuthCallback handles this
    }
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    api.get("/meta/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const login = (u, token) => { setToken(token); setUser(u); };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* best-effort; local token cleared regardless */ }
    setToken(null); setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, config, authError, refreshUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
